import { useState } from 'react'
import { Search, Download, Filter, Users, Crown, Loader2, ChevronLeft, ChevronRight } from 'lucide-react'
import { SupabaseClient } from '@supabase/supabase-js'
import { useUsers } from '@/lib/data'

interface UsersViewProps {
  client: SupabaseClient | null
}

export function UsersView({ client }: UsersViewProps) {
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [searchInput, setSearchInput] = useState('')
  const [tier, setTier] = useState<'all' | 'free' | 'premium' | 'beta'>('all')
  const limit = 20

  const { users, total, isLoading, error } = useUsers(client, {
    page,
    limit,
    search,
    tier,
  })

  const totalPages = Math.ceil(total / limit)

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    setSearch(searchInput)
    setPage(1)
  }

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Never'
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })
  }

  if (!client) {
    return (
      <div className="flex flex-col items-center justify-center h-[50vh] text-[var(--text-muted)]">
        <Users size={48} className="mb-4 opacity-20" />
        <p className="text-lg font-medium">Not Connected</p>
        <p className="text-sm">Configure Supabase to view users</p>
      </div>
    )
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-bold text-[var(--text-main)]">User Management</h2>
        <div className="flex gap-3">
          <button className="px-4 py-2 bg-[var(--bg-element)] text-[var(--text-muted)] rounded-lg hover:bg-[var(--bg-hover)] border border-[var(--border)] flex items-center gap-2">
            <Download size={16} />
            Export
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-[var(--bg-card)] border border-[var(--border)] p-4 rounded-xl">
          <p className="text-[var(--text-muted)] text-sm">Total Users</p>
          <p className="text-2xl font-bold text-[var(--text-main)] mt-1">
            {isLoading ? '...' : total.toLocaleString()}
          </p>
        </div>
        <div className="bg-[var(--bg-card)] border border-[var(--border)] p-4 rounded-xl">
          <p className="text-[var(--text-muted)] text-sm">Premium Users</p>
          <p className="text-2xl font-bold text-[var(--text-main)] mt-1">
            {isLoading ? '...' : users.filter(u => u.subscription_tier === 'premium').length}
          </p>
        </div>
        <div className="bg-[var(--bg-card)] border border-[var(--border)] p-4 rounded-xl">
          <p className="text-[var(--text-muted)] text-sm">Free Users</p>
          <p className="text-2xl font-bold text-[var(--text-main)] mt-1">
            {isLoading ? '...' : users.filter(u => u.subscription_tier === 'free').length}
          </p>
        </div>
        <div className="bg-[var(--bg-card)] border border-[var(--border)] p-4 rounded-xl">
          <p className="text-[var(--text-muted)] text-sm">Beta Users</p>
          <p className="text-2xl font-bold text-[var(--text-main)] mt-1">
            {isLoading ? '...' : users.filter(u => u.subscription_tier === 'beta').length}
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4 items-center">
        <form onSubmit={handleSearch} className="relative flex-1 min-w-[200px] max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[var(--text-muted)]" size={18} />
          <input
            type="text"
            placeholder="Search by email or name..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="w-full bg-[var(--bg-element)] border border-[var(--border)] text-[var(--text-main)] pl-10 pr-4 py-2 rounded-lg focus:outline-none focus:border-[var(--primary)]"
          />
        </form>

        <select
          value={tier}
          onChange={(e) => {
            setTier(e.target.value as typeof tier)
            setPage(1)
          }}
          className="bg-[var(--bg-element)] border border-[var(--border)] text-[var(--text-main)] px-4 py-2 rounded-lg focus:outline-none focus:border-[var(--primary)]"
        >
          <option value="all">All Tiers</option>
          <option value="free">Free</option>
          <option value="premium">Premium</option>
          <option value="beta">Beta</option>
        </select>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-500/10 border border-red-500/20 text-red-400 p-4 rounded-lg">
          {error}
        </div>
      )}

      {/* Table */}
      <div className="bg-[var(--bg-card)] border border-[var(--border)] rounded-xl overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-[var(--primary)]" />
          </div>
        ) : users.length === 0 ? (
          <div className="text-center py-12 text-[var(--text-muted)]">
            No users found
          </div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="border-b border-[var(--border)]">
                <th className="text-left p-4 text-[var(--text-muted)] font-medium text-sm">User</th>
                <th className="text-left p-4 text-[var(--text-muted)] font-medium text-sm">Subscription</th>
                <th className="text-left p-4 text-[var(--text-muted)] font-medium text-sm">Scans</th>
                <th className="text-left p-4 text-[var(--text-muted)] font-medium text-sm">Joined</th>
                <th className="text-left p-4 text-[var(--text-muted)] font-medium text-sm">Last Active</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr key={user.id} className="border-b border-[var(--border)] hover:bg-[var(--bg-element)]">
                  <td className="p-4">
                    <div>
                      <p className="text-[var(--text-main)] font-medium">
                        {user.first_name || user.last_name
                          ? `${user.first_name || ''} ${user.last_name || ''}`.trim()
                          : 'Anonymous'}
                      </p>
                      <p className="text-[var(--text-muted)] text-sm">{user.email}</p>
                    </div>
                  </td>
                  <td className="p-4">
                    <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs rounded-full ${
                      user.subscription_tier === 'premium'
                        ? 'bg-amber-500/10 text-amber-400'
                        : user.subscription_tier === 'beta'
                        ? 'bg-purple-500/10 text-purple-400'
                        : 'bg-[var(--bg-element)] text-[var(--text-muted)]'
                    }`}>
                      {user.subscription_tier === 'premium' && <Crown size={12} />}
                      {user.subscription_tier}
                      {user.billing_period && ` (${user.billing_period})`}
                    </span>
                  </td>
                  <td className="p-4 text-[var(--text-muted)]">{user.total_scans}</td>
                  <td className="p-4 text-[var(--text-muted)]">{formatDate(user.created_at)}</td>
                  <td className="p-4 text-[var(--text-muted)]">{formatDate(user.last_active_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-[var(--text-muted)] text-sm">
            Showing {(page - 1) * limit + 1} to {Math.min(page * limit, total)} of {total} users
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className="p-2 bg-[var(--bg-element)] border border-[var(--border)] rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[var(--bg-hover)]"
            >
              <ChevronLeft size={16} className="text-[var(--text-muted)]" />
            </button>
            <span className="text-[var(--text-main)] px-4">
              Page {page} of {totalPages}
            </span>
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="p-2 bg-[var(--bg-element)] border border-[var(--border)] rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[var(--bg-hover)]"
            >
              <ChevronRight size={16} className="text-[var(--text-muted)]" />
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
